import { useEffect, useState, useCallback } from 'react'
import ictaApi from '../api/ictaApi'
import {
	APIError,
	CreateUserPayload,
	CreateUserResponse,
	DeactivateUserResponse,
	GetUserResponse,
	GetUsersResponse,
	UpdateUserPayload,
	UpdateUserResponse,
	User
} from '../models/user'

export const useUser = () => {
	// Estados
	const [users, setUsers] = useState<User[]>([])
	const [total, setTotal] = useState<number>(0)
	const [selectedUser, setSelectedUser] = useState<User | null>(null)
	const [loadingList, setLoadingList] = useState<boolean>(false)
	const [loadingUser, setLoadingUser] = useState<boolean>(false)
	const [creating, setCreating] = useState<boolean>(false)
	const [updating, setUpdating] = useState<boolean>(false)
	const [deactivating, setDeactivating] = useState<boolean>(false)
	const [error, setError] = useState<string | null>(null)

	// Función helper para parsear errores
	const parseErrors = (errors?: APIError[]): string | null => {
		if (!errors || errors.length === 0) return null
		return errors[0].msg || 'Error desconocido'
	}

	// Listar usuarios
	const fetchUsers = useCallback(async () => {
		setLoadingList(true)
		setError(null)
		try {
			const { data } = await ictaApi.get<GetUsersResponse>('/user')
			const errMsg = parseErrors(data.errors)
			if (errMsg) {
				setError(errMsg)
			} else {
				setUsers(data.users)
				setTotal(data.total)
			}
		} catch (err: any) {
			console.error('Error fetching users:', err)
			setError(parseErrors(err.response.data.errors) || 'Error al obtener usuarios')
		} finally {
			setLoadingList(false)
		}
	}, [])

	// Obtener usuario por ID
	const fetchUser = useCallback(async (id: string) => {
		setLoadingUser(true)
		setError(null)
		try {
			const { data } = await ictaApi.get<GetUserResponse>(`/user/${id}`)
			const errMsg = parseErrors(data.errors)
			if (errMsg) {
				setError(errMsg)
			} else {
				setSelectedUser(data.user)
			}
		} catch (err: any) {
			setError(parseErrors(err.response.data.errors) || 'Error al obtener el usuario')
		} finally {
			setLoadingUser(false)
		}
	}, [])

	// Crear usuario
	const createUser = useCallback(async (payload: CreateUserPayload) => {
		setCreating(true)
		setError(null)
		try {
			const { data } = await ictaApi.post<CreateUserResponse>('/user', payload)
			const errMsg = parseErrors(data.errors)
			if (errMsg) {
				setError(errMsg)
				return null
			}
			setUsers((prev) => [data.user, ...prev])
			return data
		} catch (err: any) {
			setError(parseErrors(err.response.data.errors) || 'Error al crear el usuario')
			return null
		} finally {
			setCreating(false)
		}
	}, [])

	// Actualizar usuario
	const updateUser = useCallback(
		async (id: string, payload: UpdateUserPayload) => {
			setUpdating(true)
			setError(null)
			try {
				const { data } = await ictaApi.put<UpdateUserResponse>(`/user/${id}`, payload)
				const errMsg = parseErrors(data.errors)
				if (errMsg) {
					setError(errMsg)
					return null
				}
				setUsers((prev) => prev.map((u) => (u.uid === id ? data : u)))
				if (selectedUser?.uid === id) setSelectedUser(data)
				return data
			} catch (err: any) {
				setError(parseErrors(err.response.data.errors) || 'Error al actualizar el usuario')
				return null
			} finally {
				setUpdating(false)
			}
		},
		[selectedUser]
	)

	// Desactivar usuario
	const deactivateUser = useCallback(
		async (id: string) => {
			setDeactivating(true)
			setError(null)
			try {
				const { data } = await ictaApi.delete<DeactivateUserResponse>(`/user/${id}`)
				const errMsg = parseErrors(data.errors)
				if (errMsg) {
					setError(errMsg)
					return null
				}
				setUsers((prev) => prev.filter((u) => u.uid !== id))
				if (selectedUser?.uid === id) setSelectedUser(data)
				return data
			} catch (err: any) {
				setError(parseErrors(err.response.data.errors) || 'Error al desactivar el usuario')
				return null
			} finally {
				setDeactivating(false)
			}
		},
		[selectedUser]
	)

	// Auto-fetch de lista al montar
	useEffect(() => {
		fetchUsers()
	}, [fetchUsers])

	// Alert de error
	useEffect(() => {
		if (error) {
			alert(`Error: ${error}`)
			setError(null) // Reset error after showing alert
		}
	}, [error])

	return {
		// Datos
		users,
		total,
		selectedUser,
		// Estados
		loadingList,
		loadingUser,
		creating,
		updating,
		deactivating,
		error,
		// Acciones
		fetchUsers,
		fetchUser,
		createUser,
		updateUser,
		deactivateUser
	}
}
